import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { useToast } from '@/hooks/use-toast';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Radio,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  MapPin,
  Navigation,
  Fuel,
  Cross,
  Home,
  Building2,
  Newspaper,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Bot,
  Send,
  Loader2,
  ChevronRight,
  RefreshCw,
  Target,
  Route,
  Droplets,
  Warehouse,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Threat level colors and icons
const THREAT_CONFIG = {
  MINIMAL: { color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: ShieldCheck, label: 'MINIMAL' },
  LOW: { color: 'bg-blue-500', textColor: 'text-blue-400', icon: Shield, label: 'LOW' },
  GUARDED: { color: 'bg-yellow-500', textColor: 'text-yellow-400', icon: Shield, label: 'GUARDED' },
  ELEVATED: { color: 'bg-orange-500', textColor: 'text-orange-400', icon: ShieldAlert, label: 'ELEVATED' },
  SEVERE: { color: 'bg-red-500', textColor: 'text-red-400', icon: ShieldAlert, label: 'SEVERE' },
  CRITICAL: { color: 'bg-red-900', textColor: 'text-red-300', icon: AlertTriangle, label: 'CRITICAL' }
};

// Factor type icons
const FACTOR_ICONS = {
  weather: AlertTriangle,
  civil: Building2,
  infrastructure: Zap,
  crime: ShieldAlert
};

// Threat level indicator component
const ThreatLevelIndicator = ({ level, size = 'md' }) => {
  const config = THREAT_CONFIG[level] || THREAT_CONFIG.MINIMAL;
  const Icon = config.icon;
  
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-16 h-16 text-sm',
    lg: 'w-24 h-24 text-lg'
  };
  
  return (
    <div className={`${sizes[size]} ${config.color} rounded-full flex items-center justify-center relative`}>
      <Icon className="w-1/2 h-1/2 text-white" />
      <span className={`absolute -bottom-6 ${config.textColor} font-bold`}>{level}</span>
    </div>
  );
};

// Location assessment card
const LocationCard = ({ assessment }) => {
  const config = THREAT_CONFIG[assessment.threat_level] || THREAT_CONFIG.MINIMAL;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-4 h-4 text-teal-400" />
            {assessment.name || 'Unknown Location'}
          </CardTitle>
          <Badge className={`${config.color} text-white`}>
            {assessment.threat_level}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-400 italic mb-3">"{assessment.tank_assessment}"</p>
        
        <div className="space-y-2">
          {assessment.factors?.map((factor, idx) => {
            const FactorIcon = FACTOR_ICONS[factor.type] || AlertTriangle;
            return (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <FactorIcon className={`w-4 h-4 ${factor.impact > 2 ? 'text-orange-400' : 'text-slate-400'}`} />
                <span className="text-slate-300">{factor.name}</span>
                {factor.impact > 0 && (
                  <Badge variant="outline" className="text-xs ml-auto">
                    Impact: {factor.impact}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Prediction card
const PredictionCard = ({ prediction }) => {
  const confidenceColor = prediction.confidence >= 80 ? 'text-emerald-400' : prediction.confidence >= 60 ? 'text-yellow-400' : 'text-orange-400';
  
  return (
    <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-teal-400 font-medium">{prediction.timeframe}</span>
        <span className={`text-xs ${confidenceColor}`}>{prediction.confidence}% confidence</span>
      </div>
      <p className="text-sm text-slate-300">{prediction.prediction}</p>
    </div>
  );
};

// News card
const NewsCard = ({ article }) => {
  const relevanceColors = {
    high: 'border-l-red-500',
    medium: 'border-l-yellow-500',
    low: 'border-l-slate-500'
  };
  
  return (
    <div className={`bg-slate-800/30 rounded-lg p-4 border border-slate-700 border-l-4 ${relevanceColors[article.relevance]}`}>
      <h4 className="font-medium text-white mb-1">{article.headline}</h4>
      <p className="text-sm text-slate-400 mb-2">{article.summary}</p>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{article.source}</span>
        <Badge variant="outline" className="text-xs">{article.category}</Badge>
      </div>
    </div>
  );
};

// Voice command input
const VoiceCommandInput = ({ onCommand, loading }) => {
  const [text, setText] = useState('');
  const [isListening, setIsListening] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim()) {
      onCommand(text.trim());
      setText('');
    }
  };
  
  const toggleListening = () => {
    // In production, this would use Web Speech API
    setIsListening(!isListening);
    if (!isListening) {
      // Start listening...
      setTimeout(() => {
        setText("What's the threat level near me?");
        setIsListening(false);
      }, 2000);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask Tank anything... (e.g., 'What's happening near me?')"
          className="bg-slate-800 border-slate-700 pr-10"
          disabled={loading}
        />
        <button
          type="button"
          onClick={toggleListening}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full ${isListening ? 'bg-red-500 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
      </div>
      <Button type="submit" disabled={loading || !text.trim()} className="bg-teal-600 hover:bg-teal-700">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </Button>
    </form>
  );
};

// Resource finder
const ResourceFinder = ({ onFind }) => {
  const [resourceType, setResourceType] = useState('gas');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  const resourceTypes = [
    { id: 'gas', label: 'Gas Stations', icon: Fuel },
    { id: 'medical', label: 'Medical', icon: Cross },
    { id: 'shelter', label: 'Shelters', icon: Warehouse },
    { id: 'water', label: 'Water', icon: Droplets }
  ];
  
  const handleFind = async () => {
    setLoading(true);
    const result = await onFind(resourceType);
    setResults(result);
    setLoading(false);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {resourceTypes.map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            variant={resourceType === id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setResourceType(id)}
            className={resourceType === id ? 'bg-teal-600' : 'border-slate-600'}
          >
            <Icon className="w-4 h-4 mr-1" />
            {label}
          </Button>
        ))}
      </div>
      
      <Button onClick={handleFind} disabled={loading} className="w-full bg-slate-700 hover:bg-slate-600">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
        Find Nearest {resourceTypes.find(r => r.id === resourceType)?.label}
      </Button>
      
      {results && (
        <div className="space-y-2">
          {results.resources?.map((resource, idx) => (
            <div key={idx} className="bg-slate-800/30 rounded-lg p-3 border border-slate-700 flex items-center justify-between">
              <div>
                <p className="font-medium text-white">{resource.name}</p>
                <p className="text-sm text-slate-400">{resource.distance}</p>
              </div>
              <Badge variant="outline" className={resource.status === 'open' ? 'border-emerald-500 text-emerald-400' : 'border-slate-500'}>
                {resource.status}
              </Badge>
            </div>
          ))}
          {results.tank_tip && (
            <p className="text-xs text-teal-400 italic mt-2">💡 {results.tank_tip}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Main Command Center component
const CommandCenter = () => {
  const { token } = useAuth();
  const { hasFeature, isPaid } = useSubscription();
  const { toast } = useToast();
  
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commandResponse, setCommandResponse] = useState(null);
  const [commandLoading, setCommandLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('briefing');
  
  const headers = { 'Authorization': `Bearer ${token}` };
  
  // Fetch daily briefing
  const fetchBriefing = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/command-center/briefing`, { headers });
      if (res.ok) {
        const data = await res.json();
        setBriefing(data);
      }
    } catch (err) {
      console.error('Failed to fetch briefing:', err);
      toast({
        title: 'Briefing failed',
        description: 'Could not load daily briefing. Try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);
  
  useEffect(() => {
    if (isPaid()) {
      fetchBriefing();
    } else {
      setLoading(false);
    }
  }, [fetchBriefing, isPaid]);
  
  // Process voice command
  const handleVoiceCommand = async (text) => {
    setCommandLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/command-center/voice-command`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCommandResponse(data);
      }
    } catch (err) {
      console.error('Command failed:', err);
    } finally {
      setCommandLoading(false);
    }
  };
  
  // Find resources
  const handleFindResources = async (resourceType) => {
    try {
      // Use default location for demo
      const res = await fetch(`${API_URL}/api/command-center/find-resources?resource_type=${resourceType}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: 39.8283, longitude: -98.5795, name: "Current Location" })
      });
      
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.error('Resource search failed:', err);
    }
    return null;
  };
  
  // Check if user has access
  if (!isPaid()) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <div className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 bg-teal-500/20 rounded-full mb-4">
              <Bot className="w-12 h-12 text-teal-400" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Tank's Command Center</h1>
            <p className="text-slate-400">Your personal AI intelligence operations hub</p>
          </div>
          
          <UpgradePrompt feature="ai_chat" />
        </div>
      </div>
    );
  }
  
  const overallConfig = THREAT_CONFIG[briefing?.overall_status] || THREAT_CONFIG.MINIMAL;
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-500/20 rounded-lg">
                  <Bot className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Tank's Command Center</h1>
                  <p className="text-xs text-slate-400">AI Intelligence Operations Hub</p>
                </div>
              </div>
            </div>
            
            {briefing && (
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-slate-400">Overall Status</p>
                  <Badge className={`${overallConfig.color} text-white`}>
                    {briefing.overall_status}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={fetchBriefing}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Voice Command Bar */}
      <div className="border-b border-slate-800 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <VoiceCommandInput onCommand={handleVoiceCommand} loading={commandLoading} />
          
          {commandResponse && (
            <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-teal-500/20 rounded-full">
                  <Bot className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="font-medium text-teal-400 mb-1">Tank says:</p>
                  <p className="text-slate-300">{commandResponse.response}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-800/50 border border-slate-700 mb-6">
              <TabsTrigger value="briefing" className="data-[state=active]:bg-teal-600">
                <Newspaper className="w-4 h-4 mr-2" />
                Daily Briefing
              </TabsTrigger>
              <TabsTrigger value="threats" className="data-[state=active]:bg-teal-600">
                <Shield className="w-4 h-4 mr-2" />
                Threat Analysis
              </TabsTrigger>
              <TabsTrigger value="resources" className="data-[state=active]:bg-teal-600">
                <Target className="w-4 h-4 mr-2" />
                Resource Finder
              </TabsTrigger>
              <TabsTrigger value="routes" className="data-[state=active]:bg-teal-600">
                <Route className="w-4 h-4 mr-2" />
                Bug-Out Routes
              </TabsTrigger>
            </TabsList>
            
            {/* Daily Briefing Tab */}
            <TabsContent value="briefing" className="space-y-6">
              {briefing && (
                <>
                  {/* Greeting */}
                  <div className="bg-gradient-to-r from-teal-900/30 to-slate-900/30 rounded-xl p-6 border border-teal-800/50">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-teal-500/20 rounded-full">
                        <Bot className="w-8 h-8 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-lg text-teal-300 font-medium mb-1">{briefing.greeting}</p>
                        <p className="text-sm text-slate-400">{briefing.date} • {briefing.time}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Location Assessments */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-teal-400" />
                      Location Assessments
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {briefing.location_assessments?.map((assessment, idx) => (
                        <LocationCard key={idx} assessment={assessment} />
                      ))}
                    </div>
                  </div>
                  
                  {/* Predictions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-teal-400" />
                      AI Predictions
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {briefing.predictions?.map((prediction, idx) => (
                        <PredictionCard key={idx} prediction={prediction} />
                      ))}
                    </div>
                  </div>
                  
                  {/* News Intel */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Newspaper className="w-5 h-5 text-teal-400" />
                      Intelligence Summary
                    </h3>
                    <div className="space-y-3">
                      {briefing.news_summary?.map((article, idx) => (
                        <NewsCard key={idx} article={article} />
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Items */}
                  <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      Today's Action Items
                    </h3>
                    <ul className="space-y-2">
                      {briefing.action_items?.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300">
                          <ChevronRight className="w-4 h-4 text-teal-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Sign off */}
                  <div className="text-center py-6">
                    <p className="text-slate-400 italic mb-2">"{briefing.tank_quote}"</p>
                    <p className="text-teal-400 font-medium">{briefing.sign_off}</p>
                  </div>
                </>
              )}
            </TabsContent>
            
            {/* Threat Analysis Tab */}
            <TabsContent value="threats" className="space-y-6">
              <div className="text-center py-8">
                <ThreatLevelIndicator level={briefing?.overall_status || 'MINIMAL'} size="lg" />
                <h2 className="text-2xl font-bold mt-8">Current Threat Level</h2>
                <p className="text-slate-400 mt-2 max-w-md mx-auto">
                  {THREAT_CONFIG[briefing?.overall_status]?.label || 'MINIMAL'} - Based on analysis of weather, civil activity, infrastructure, and crime data.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {briefing?.location_assessments?.map((assessment, idx) => (
                  <LocationCard key={idx} assessment={assessment} />
                ))}
              </div>
            </TabsContent>
            
            {/* Resource Finder Tab */}
            <TabsContent value="resources">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-teal-400" />
                    Emergency Resource Locator
                  </CardTitle>
                  <CardDescription>
                    Find gas stations, medical facilities, shelters, and water sources near you.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResourceFinder onFind={handleFindResources} />
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Bug-Out Routes Tab */}
            <TabsContent value="routes">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Route className="w-5 h-5 text-teal-400" />
                    Bug-Out Route Planner
                  </CardTitle>
                  <CardDescription>
                    Calculate optimal evacuation routes that avoid known threats.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-slate-400">
                    <Navigation className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Route planning coming soon.</p>
                    <p className="text-sm mt-2">Set up your saved locations in the dashboard to enable this feature.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default CommandCenter;
